import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucideRefreshCw,
  lucideExternalLink,
  lucideBanknote,
  lucideBell,
  lucideStore,
  lucideChartColumn,
  lucideCheck,
  lucideClipboard,
  lucideSettings,
  lucideMail,
  lucideTriangleAlert,
  lucideGlobe,
  lucideKey,
  lucidePhone,
  lucideServer,
  lucideShieldCheck,
  lucideWallet,
  lucideX,
} from '@ng-icons/lucide';

import { UI, type UiBadgeVariant } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';
import {
  MerchantApplicationService,
  type MerchantApplication,
  type MerchantStats,
} from '../../services/merchant-application.service';
import { FeeAnalyticsService, type FeeConfigurationRow, type FeeSummaryResponse } from '../../services/fee-analytics.service';
import { formatHumanAmount } from '../../utils/admin-formatting';

/** Merchant full data. */
interface MerchantData {
  id: string;
  name: string;
  legal_name: string;
  email: string;
  phone_number: string | null;
  country: number;
  country_code: string;
  country_name: string;
  registration_number: string;
  tax_id: string;
  webhook_url: string;
  has_webhook_secret: boolean;
  is_active: boolean;
  metadata: Record<string, unknown>;
  application_count: number;
  created_at: string;
  updated_at: string;
}

/** Consolidated wallet card model for merchant hub. */
interface WalletCardData {
  walletId: string;
  currencyCode: string;
  countryCode: string;
  walletBalance: number;
  ledgerBalance: number;
  difference: number;
  isTaggedMismatch: boolean;
}

/** Wallet totals grouped by market (currency + country). */
interface MarketWalletGroup {
  marketCode: string;
  currencyCode: string;
  countryCode: string;
  walletCount: number;
  totalBalance: number;
  totalLedger: number;
  hasMismatch: boolean;
  wallets: WalletCardData[];
}

/**
 * Merchant Detail Hub - Full BI dashboard for a merchant.
 *
 * Sections:
 * - Merchant Info (name, contact, registration)
 * - Applications summary with links
 * - Wallet overview with balances by currency
 * - Transaction stats (deposits, payouts, refunds, remittances)
 * - API key summary
 * - Recent audit log
 */
@Component({
  selector: 'app-merchant-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NgIcon, ...UI],
  templateUrl: './merchant-detail.component.html',
  viewProviders: [
    provideIcons({
      lucideArrowLeft,
      lucideRefreshCw,
      lucideExternalLink,
      lucideBanknote,
      lucideBell,
      lucideStore,
      lucideChartColumn,
      lucideCheck,
      lucideClipboard,
      lucideSettings,
      lucideMail,
      lucideTriangleAlert,
      lucideGlobe,
      lucideKey,
      lucidePhone,
      lucideServer,
      lucideShieldCheck,
      lucideWallet,
      lucideX,
    }),
  ],
})
export class MerchantDetailComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly svc = inject(MerchantApplicationService);
  private readonly feeAnalytics = inject(FeeAnalyticsService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  readonly shellRoutes = inject(ShellRoutesService);

  /* ─── Data signals ─── */
  readonly merchant = signal<MerchantData | null>(null);
  readonly apps = signal<MerchantApplication[]>([]);
  readonly stats = signal<MerchantStats | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly feeSummary = signal<FeeSummaryResponse | null>(null);
  readonly feeConfigs = signal<FeeConfigurationRow[]>([]);

  /* ─── Stats period ─── */
  statsPeriod = '30d';

  /* ─── Webhook config ─── */
  readonly webhookEditing = signal(false);
  readonly webhookSaving = signal(false);
  readonly webhookRegenerating = signal(false);
  readonly webhookSuccess = signal<string | null>(null);
  readonly webhookError = signal<string | null>(null);
  readonly generatedSecret = signal<string | null>(null);
  webhookUrlEdit = '';

  /* ─── Copy toast ─── */
  readonly copyToast = signal(false);
  private copyToastTimeout: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.loadMerchant();
    this.loadApps();
    this.loadStats();
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityConfig.key));
  }

  // ─── Data Loading ──────────────────────────────────────────

  loadMerchant(): void {
    this.loading.set(true);
    this.error.set(null);
    this.svc.getMerchant(this.id).subscribe({
      next: (data) => {
        this.merchant.set(data as unknown as MerchantData);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to load merchant.');
        this.loading.set(false);
      },
    });
  }

  loadApps(): void {
    this.svc.getMerchantApplications(this.id).subscribe({
      next: (data) => this.apps.set(data),
      error: () => this.apps.set([]),
    });
  }

  loadStats(): void {
    this.svc.getMerchantStats(this.id, this.statsPeriod).subscribe({
      next: (data) => this.stats.set(data),
      error: () => this.stats.set(null),
    });
    this.feeAnalytics.getSummary({ period: this.normalizePeriod(), merchant_id: this.id }).subscribe({
      next: (res) => this.feeSummary.set(res),
      error: () => this.feeSummary.set(null),
    });
    this.feeAnalytics.getConfigurations({ merchant_id: this.id }).subscribe({
      next: (res) => this.feeConfigs.set(res.rows.slice(0, 8)),
      error: () => this.feeConfigs.set([]),
    });
  }

  onPeriodChange(): void {
    this.loadStats();
  }

  refreshAll(): void {
    this.loadMerchant();
    this.loadApps();
    this.loadStats();
  }

  // ─── Helpers ───────────────────────────────────────────────

  copyToClipboard(value: string): void {
    navigator.clipboard.writeText(value).then(() => {
      if (this.copyToastTimeout) clearTimeout(this.copyToastTimeout);
      this.copyToast.set(true);
      this.copyToastTimeout = setTimeout(() => this.copyToast.set(false), 1500);
    });
  }

  getEnvVariant(env: string): UiBadgeVariant {
    switch (env) {
      case 'production':
        return 'info';
      case 'sandbox':
        return 'warning';
      case 'development':
        return 'brand';
      default:
        return 'neutral';
    }
  }

  formatDate(value: string | null): string {
    if (!value) return '—';
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return (
        d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) +
        ' ' +
        d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
      );
    } catch {
      return value;
    }
  }

  formatAmount(value: number | null | undefined): string {
    if (!value) return '0.00';
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatMoney(value: string, currencyCode: string): string {
    return formatHumanAmount(value, currencyCode);
  }

  private normalizePeriod(): string {
    if (this.statsPeriod === 'today') return 'today';
    if (this.statsPeriod === '7d') return 'week';
    if (this.statsPeriod === '30d') return 'month';
    return 'month';
  }

  getTotalTx(): number {
    const s = this.stats();
    if (!s) return 0;
    const t = s.transactions;
    return t.deposits.total + t.payouts.total + t.refunds.total + t.remittances.total;
  }

  getTotalSuccessVol(): number {
    const s = this.stats();
    if (!s) return 0;
    const t = s.transactions;
    return t.deposits.success_amount + t.payouts.success_amount + t.refunds.success_amount + t.remittances.success_amount;
  }

  getSuccessRate(): string {
    const s = this.stats();
    if (!s) return '—';
    const t = s.transactions;
    const total = t.deposits.total + t.payouts.total + t.refunds.total + t.remittances.total;
    if (total === 0) return '—';
    const success = t.deposits.success + t.payouts.success + t.refunds.success + t.remittances.success;
    return ((success / total) * 100).toFixed(1) + '%';
  }

  getTotalAvailableBalance(): number {
    const s = this.stats();
    if (!s) return 0;
    return s.wallets.total_balance - s.wallets.total_reserved;
  }

  walletRoute(walletId: string): (string | number | boolean)[] {
    return this.shellRoutes.commands('manage', 'wallets', walletId);
  }

  getMarketWalletGroups(): MarketWalletGroup[] {
    const cards = this.getWalletCards();
    const groups = new Map<string, MarketWalletGroup>();

    for (const card of cards) {
      const marketCode = `${card.currencyCode}_${card.countryCode}`;
      const existing = groups.get(marketCode);
      if (existing) {
        existing.wallets.push(card);
        existing.walletCount += 1;
        existing.totalBalance += card.walletBalance;
        existing.totalLedger += card.ledgerBalance;
        existing.hasMismatch = existing.hasMismatch || card.isTaggedMismatch;
      } else {
        groups.set(marketCode, {
          marketCode,
          currencyCode: card.currencyCode,
          countryCode: card.countryCode,
          walletCount: 1,
          totalBalance: card.walletBalance,
          totalLedger: card.ledgerBalance,
          hasMismatch: card.isTaggedMismatch,
          wallets: [card],
        });
      }
    }

    return Array.from(groups.values()).sort((a, b) => a.marketCode.localeCompare(b.marketCode));
  }

  marketLabel(group: MarketWalletGroup): string {
    return `${group.currencyCode} · ${group.countryCode}`;
  }

  getWalletCards(): WalletCardData[] {
    const s = this.stats();
    if (!s) return [];
    return s.wallet_differences.map((walletDifference) => ({
      walletId: walletDifference.wallet_id,
      currencyCode: walletDifference.currency_code,
      countryCode: walletDifference.country_code,
      walletBalance: walletDifference.wallet_balance,
      ledgerBalance: walletDifference.ledger_balance,
      difference: walletDifference.difference,
      isTaggedMismatch: walletDifference.is_tagged_mismatch,
    }));
  }

  getWalletLabel(card: WalletCardData, allInMarket: WalletCardData[]): string {
    const base = `${card.currencyCode}-${card.countryCode}`;
    if (allInMarket.length <= 1) return base;
    const suffix = card.walletId ? card.walletId.slice(0, 6) : '';
    return suffix ? `${base}-${suffix}` : base;
  }

  // ─── Webhook Config ─────────────────────────────────────────

  startWebhookEdit(): void {
    const m = this.merchant();
    this.webhookUrlEdit = m?.webhook_url ?? '';
    this.webhookEditing.set(true);
    this.webhookSuccess.set(null);
    this.webhookError.set(null);
    this.generatedSecret.set(null);
  }

  cancelWebhookEdit(): void {
    this.webhookEditing.set(false);
    this.webhookError.set(null);
  }

  saveWebhookConfig(): void {
    const m = this.merchant();
    if (!m) return;

    this.webhookSaving.set(true);
    this.webhookError.set(null);
    this.webhookSuccess.set(null);

    this.svc.updateMerchantWebhook(this.id, { webhook_url: this.webhookUrlEdit }).subscribe({
      next: (res) => {
        this.merchant.update((current) =>
          current ? { ...current, webhook_url: res.webhook_url, has_webhook_secret: res.has_webhook_secret } : current,
        );
        this.webhookSaving.set(false);
        this.webhookEditing.set(false);
        this.webhookSuccess.set('Webhook URL updated successfully.');
        setTimeout(() => this.webhookSuccess.set(null), 4000);
      },
      error: (err) => {
        this.webhookError.set(err?.error?.detail ?? err?.message ?? 'Failed to update webhook URL.');
        this.webhookSaving.set(false);
      },
    });
  }

  regenerateSecret(): void {
    this.webhookRegenerating.set(true);
    this.webhookError.set(null);
    this.webhookSuccess.set(null);
    this.generatedSecret.set(null);

    this.svc.regenerateWebhookSecret(this.id).subscribe({
      next: (res) => {
        this.merchant.update((current) => (current ? { ...current, has_webhook_secret: true } : current));
        this.generatedSecret.set(res.webhook_secret);
        this.webhookRegenerating.set(false);
        this.webhookSuccess.set(res.message);
      },
      error: (err) => {
        this.webhookError.set(err?.error?.detail ?? err?.message ?? 'Failed to regenerate webhook secret.');
        this.webhookRegenerating.set(false);
      },
    });
  }

  dismissGeneratedSecret(): void {
    this.generatedSecret.set(null);
  }
}
